import ConfirmModal from '@/components/ConfirmModal';
import { Icons } from '@/components/icons';
import { Button } from '@/components/primitives/Button';
import { Typography } from '@/components/primitives/Typography';
import {
  deletePurchaseFlowBooking,
  resetPurchase,
} from '@/features/purchase/purchaseActions';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { persistor } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import { type FC, useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const CancelPurchaseButton: FC = () => {
  const dispatch = useDispatch();
  const booking = useSelector(purchaseFlowBookingSelector);
  const { setCurrentSearchFormValues } = useSearchWidget();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const navigate = useNavigate();

  const handleCancelPurchase = useCallback(async () => {
    if (!booking?.id) {
      return;
    }

    try {
      await dispatch(deletePurchaseFlowBooking(booking.id)).unwrap();
      dispatch(resetPurchase());
      setCurrentSearchFormValues(undefined);
      void persistor.purge();
      navigate('/purchase');
    } catch {
      toast.error(<TransAlert i18nKey="purchaseCancellationFailed" />);
    }
  }, [booking?.id, dispatch, navigate, setCurrentSearchFormValues]);

  return (
    <ConfirmModal
      title={<TransText i18nKey="cancelPurchase" />}
      description={
        <Typography variant="paragraph">
          <TransText i18nKey="areYouSureYouWantToCancelThePurchase" />
        </Typography>
      }
      cancelLabel="dismiss"
      confirmLabel="confirm"
      onConfirm={handleCancelPurchase}
    >
      <Button
        size={isLaptopOrBigger ? 'medium' : 'large'}
        fullWidth
        data-testid="cancel-purchase-button"
        className="rounded-lg laptop:w-auto"
        variant="tertiary"
      >
        <Icons.cancel />
        <TransText i18nKey="cancel" />
      </Button>
    </ConfirmModal>
  );
};

export default CancelPurchaseButton;
